import React from "react";
import PropTypes from "prop-types";

import { Toolbar, QuickFilter, QuickFilterClear, QuickFilterControl } from "@mui/x-data-grid";
import { Box, InputAdornment, TextField } from "@mui/material";
import { SearchIcon as Search } from "components/icons";

/**
 * The Master Data search box, extracted so every DataGrid in the app presents the
 * same control instead of re-declaring the QuickFilter markup.
 *
 * Drop it inside a grid toolbar. The enclosing DataGrid decides what the typed
 * text does: server grids pair it with filterMode="server" plus a controlled
 * filterModel, client grids let MUI filter the loaded rows.
 */
// Search the typed text as one phrase. The MUI default splits it on spaces into
// independent terms and keeps a row when every term matches *some* column, so
// "akash g" matched "Akash Kalburgi" and "AXT Gargi" matched a row whose client was
// AXT and whose name was Gargi (HRMS-310). A phrase has to sit in a single column.
const PHRASE_PARSER = (input) => {
    const phrase = (input ?? "").trim();
    return phrase === "" ? [] : [phrase];
};

// @since NEXT_VERSION
export const GridQuickSearchField = ({ debounceMs = 500, placeholder = "Search..." }) => (
    <QuickFilter expanded debounceMs={debounceMs} parser={PHRASE_PARSER}>
        <QuickFilterControl
            render={(controlProps) => (
                <TextField
                    {...controlProps}
                    size="small"
                    placeholder={placeholder}
                    // 36.5px matches the contained-medium MUI Button height so the
                    // search field sits flush with any Add button beside it.
                    sx={{
                        width: 220,
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "transparent",
                            height: 36.5,
                            fontSize: "1rem",
                        },
                        "& .MuiInputBase-input": { fontSize: "1rem" },
                    }}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            )}
        />
        <QuickFilterClear />
    </QuickFilter>
);

GridQuickSearchField.propTypes = {
    debounceMs: PropTypes.number,
    placeholder: PropTypes.string,
};

/**
 * Search-only toolbar for grids that hold all their rows client side and so need
 * no Add button or server round trip.
 */
// @since NEXT_VERSION
export const GridSearchToolbar = ({ placeholder }) => (
    <Toolbar>
        <Box sx={{ py: 2.5, display: "flex", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
            <GridQuickSearchField placeholder={placeholder} />
        </Box>
    </Toolbar>
);

GridSearchToolbar.propTypes = {
    placeholder: PropTypes.string,
};

export default GridQuickSearchField;
